// screen: Support — Contact support + My requests (control-plane change support-requests)
// Hebrew-first. Reached from the account menu (and the Help center), and from the error page ("Something went wrong"),
// which pre-fills category = bug and attaches the error and its request id. The page the person came from and the
// last API request ids / errors (memory only, see app/supportContext.ts) are attached automatically and shown in
// "What we'll send". The server enforces every limit and scope.
import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { LifeBuoy, MessageSquare, Send } from 'lucide-react';
import { ApiError, SUPPORT_CATEGORIES, type SupportCategory, type SupportContext, type SupportPriority } from '@viewstream/api-client';
import { Badge, Button, EmptyState, ErrorState, Field, Input, Label, Select, Skeleton, useToast } from '@viewstream/ui';
import { fmtDateTime } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { useAuth } from '~/app/auth';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { problemText } from '~/app/team';
import { collectSupportContext } from '~/app/supportContext';
import { WhatWeSend } from '~/app/SupportContextView';
import { STATUS_TONE, useCreateSupportRequest, useSupportLabels, useSupportRequests } from '~/app/support';

interface SupportSearch { category?: SupportCategory; from?: string; error?: string; rid?: string }

const str = (v: unknown, max: number): string | undefined => (typeof v === 'string' && v !== '' ? v.slice(0, max) : undefined);

export const Route = createFileRoute('/_layout/t/$slug/support')({
  component: Screen,
  validateSearch: (s: Record<string, unknown>): SupportSearch => ({
    ...(SUPPORT_CATEGORIES.includes(s['category'] as SupportCategory) ? { category: s['category'] as SupportCategory } : {}),
    ...(str(s['from'], 500)?.startsWith('/') ? { from: str(s['from'], 500) } : {}),
    ...(str(s['error'], 500) ? { error: str(s['error'], 500) } : {}),
    ...(typeof s['rid'] === 'string' && /^[\w.:-]{1,100}$/.test(s['rid']) ? { rid: s['rid'] } : {}),
  }),
});

const SUBJECT_MAX = 200;
const MESSAGE_MAX = 10000;

function Screen() {
  const { t } = useLingui();
  React.useEffect(() => { document.title = appTitle(t`Contact support`); }, [t]);
  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold"><LifeBuoy className="size-6 text-primary" aria-hidden /> {t`Contact support`}</h1>
        <p className="max-w-3xl text-sm text-fg-muted">{t`Write to the ViewStream support team at Interhost — in Hebrew or English. We reply by e-mail and here. For a service outage choose “Outage — service is down”: the on-call team is alerted right away.`}</p>
      </header>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <NewRequest />
        <MyRequests />
      </div>
    </div>
  );
}

function NewRequest() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { me } = useAuth();
  const { locale, tz } = useLocale();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const toast = useToast();
  const labels = useSupportLabels();
  const create = useCreateSupportRequest(tenant.slug);

  const prefill = React.useMemo(() => {
    if (!search.error) return '';
    const lines = ['', '', '———', t`Error shown in Studio:` + ' ' + search.error];
    if (search.rid) lines.push(t`Request id:` + ' ' + search.rid);
    return lines.join('\n');
  }, [search.error, search.rid, t]);
  const [category, setCategory] = React.useState<SupportCategory>(search.category ?? (search.error ? 'bug' : 'question'));
  const [priority, setPriority] = React.useState<SupportPriority>('normal');
  const [subject, setSubject] = React.useState(search.error ? t`Studio showed an error` : '');
  const [message, setMessage] = React.useState(prefill);
  const [email, setEmail] = React.useState(me?.user?.email ?? '');
  const [name, setName] = React.useState(me?.user?.name ?? '');
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const context = React.useMemo<SupportContext>(() => collectSupportContext(search.from, locale, tz), [search.from, locale, tz]);

  React.useEffect(() => { if (category !== 'outage' && priority === 'urgent') setPriority('high'); }, [category, priority]);

  const priorities: SupportPriority[] = category === 'outage' ? ['normal', 'high', 'urgent'] : ['normal', 'high'];
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!subject.trim()) errs['subject'] = t`Write a short subject.`;
    if (!message.trim()) errs['message'] = t`Describe what happened.`;
    if (!emailOk) errs['contact_email'] = t`Enter a valid e-mail address.`;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    create.mutate(
      { category, priority, subject: subject.trim(), message: message.trim(), contact_email: email.trim(), contact_name: name.trim(), lang: locale === 'en' ? 'en' : 'he', context },
      {
        onSuccess: (x) => {
          toast.push({ title: t`Your request was sent`, description: t`We will reply by e-mail and on the request page.`, tone: 'ok' });
          void navigate({ to: '/t/$slug/support/$requestId', params: { slug: tenant.slug, requestId: x.id } } as never);
        },
        onError: (err) => {
          const apiErr = err instanceof ApiError ? err : null;
          const fieldErrs: Record<string, string> = {};
          for (const f of apiErr?.problem?.errors ?? []) fieldErrs[f.field] = f.detail;
          setErrors(fieldErrs);
          toast.push({ title: apiErr?.status === 429 ? t`Too many requests in the last hour — add to an open request instead, or call Interhost for an outage.` : t`Could not send the request`, description: problemText(err), tone: 'danger' });
        },
      },
    );
  };

  return (
    <section aria-labelledby="sr-new" className="rounded-lg border border-border bg-surface p-4 md:p-6">
      <h2 id="sr-new" className="mb-4 text-lg font-semibold">{t`New request`}</h2>
      <form className="space-y-4" onSubmit={submit} noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>{t`Topic`}</Label>
            <Select ariaLabel={t`Topic`} className="w-full" value={category} onValueChange={(v) => setCategory(v as SupportCategory)}
              options={SUPPORT_CATEGORIES.map((c) => ({ value: c, label: labels.category[c] }))} />
          </div>
          <div>
            <Label>{t`Priority`}</Label>
            <Select ariaLabel={t`Priority`} className="w-full" value={priority} onValueChange={(v) => setPriority(v as SupportPriority)}
              options={priorities.map((p) => ({ value: p, label: labels.priority[p] }))} />
            {category === 'outage' ? <p className="mt-1 text-xs text-fg-muted">{t`“Urgent” alerts the on-call team immediately — use it when viewers cannot watch.`}</p> : null}
          </div>
        </div>
        <Field id="sr-subject" label={t`Subject`} error={errors['subject']}>
          <Input dir="auto" maxLength={SUBJECT_MAX} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t`For example: the player does not load on the main channel`} />
        </Field>
        <div>
          <Label htmlFor="sr-message">{t`Message`}</Label>
          <textarea id="sr-message" dir="auto" rows={8} maxLength={MESSAGE_MAX} value={message} onChange={(e) => setMessage(e.target.value)}
            aria-invalid={errors['message'] ? true : undefined} aria-describedby="sr-message-hint"
            className="w-full rounded-md border border-border bg-surface p-2 text-sm" />
          <p id="sr-message-hint" className="mt-1 flex justify-between gap-2 text-xs text-fg-muted">
            <span>{t`What happened, when, and on which channel or video. Never send passwords or API keys.`}</span>
            <bdi className="shrink-0 tabular-nums">{message.length}/{MESSAGE_MAX}</bdi>
          </p>
          {errors['message'] ? <p role="alert" className="mt-1 text-xs text-danger">{errors['message']}</p> : null}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="sr-email" label={t`Reply to (e-mail)`} error={errors['contact_email']}>
            <Input type="email" dir="ltr" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field id="sr-name" label={t`Your name`}>
            <Input dir="auto" autoComplete="name" maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
        </div>
        <WhatWeSend context={context} />
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" loading={create.isPending}><Send className="size-4 icon-dir" aria-hidden /> {t`Send to support`}</Button>
          <span className="text-xs text-fg-muted">{t`Sent to Interhost support together with the details above.`}</span>
        </div>
      </form>
    </section>
  );
}

function MyRequests() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale, tz } = useLocale();
  const labels = useSupportLabels();
  const q = useSupportRequests(tenant.slug);
  return (
    <section aria-labelledby="sr-mine" className="space-y-3">
      <h2 id="sr-mine" className="text-lg font-semibold">{t`My requests`}</h2>
      {q.isPending ? <Skeleton className="h-40" /> : q.isError ? (
        <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load your requests`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />
      ) : q.data.items.length === 0 ? (
        <EmptyState icon={<MessageSquare className="size-6" aria-hidden />} title={t`No requests yet`} description={t`Requests your team sends to support appear here with their replies.`} />
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border bg-surface" data-testid="support-list">
          {q.data.items.map((x) => {
            const n = x.message_count;
            return (
            <li key={x.id}>
              <Link to={'/t/$slug/support/$requestId' as never} params={{ slug: tenant.slug, requestId: x.id } as never}
                className="block space-y-1 p-3 hover:bg-surface-2">
                <span className="flex items-start justify-between gap-2">
                  <span dir="auto" className="min-w-0 truncate font-medium">{x.subject}</span>
                  <Badge tone={STATUS_TONE[x.status]} className="shrink-0">{labels.status[x.status]}</Badge>
                </span>
                <span className="block text-xs text-fg-muted">
                  {labels.category[x.category]}{x.priority !== 'normal' ? ` · ${labels.priority[x.priority]}` : ''} · <bdi>{fmtDateTime(x.last_reply_at ?? x.created_at, { locale, tz }, { dateStyle: 'short', timeStyle: 'short' })}</bdi>
                  {n ? ` · ${t`${n} replies`}` : ''}
                </span>
              </Link>
            </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
